import type { DateTime } from 'luxon';
import type { CSSProperties } from 'react';
import { getDisplayEmoji } from '@/features/events/lib/eventEmoji.lib';
import { getInstanceStart } from '@/features/events/lib/eventInstance.lib';
import type { PageExperience } from '@/features/experiences/types';
import { useDocumentContext } from '@/features/printables/contexts/DocumentContext';
import { getPrintableToggles } from '../../../printableConfigAdapter';
import type { PrintableEvent } from '../../../types';
import { eventButtonResetStyle } from '../../shared/eventButtonReset';
import { toTimingLabel } from '../../week/event.lib';

type Props = {
  event: PrintableEvent;
  experience: PageExperience;
  date: DateTime;
};

const subtitleTextColor = 'hsla(0, 0%, 35%, 1.00)';

const rootStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  paddingBottom: '0.0625em',
  lineHeight: '100%',
};

export const EventItem = ({ experience, event, date }: Props) => {
  const { onEventClick } = useDocumentContext();
  const {
    isEmojiEnabled,
    isEndTimeEnabled,
    isLocationEnabled,
    isStartTimeEnabled,
  } = getPrintableToggles(experience);
  // A source's default emoji outranks the event's own
  // (getDisplayEmoji); only a per-event override beats it.
  const emoji = getDisplayEmoji(experience, event);

  const timingLabel = toTimingLabel({
    date,
    event,
    isEndTimeEnabled,
    isStartTimeEnabled,
  });

  const content = (
    <>
      {timingLabel && (
        <span
          style={{
            fontSize: '0.7em',
            color: subtitleTextColor,
            marginBottom: '0.1em',
          }}
        >
          {timingLabel}
        </span>
      )}
      <span
        style={{
          display: 'inline-block',
        }}
      >
        <span>
          <span
            style={{
              fontWeight: '500',
              fontSize: '0.7em',
              lineHeight: '100%',
            }}
          >
            {isEmojiEnabled && emoji && (
              <span
                style={{
                  fontSize: '0.65em',
                  marginRight: '0.25em',
                }}
              >
                {emoji}
              </span>
            )}
            {event.titleText || event.title}
          </span>
          {isLocationEnabled && event.locationWithPlace?.name && (
            <span
              style={{
                color: subtitleTextColor,
                fontSize: '0.65em',
                lineHeight: '100%',
              }}
            >
              {', '}
              {event.locationWithPlace.name}
            </span>
          )}
        </span>
      </span>
    </>
  );

  if (onEventClick) {
    return (
      <button
        // A button shrink-to-fits while the div stretches to the cell width,
        // so stretch it explicitly to keep layout and click target identical.
        style={{ ...eventButtonResetStyle, ...rootStyle, width: '100%' }}
        type="button"
        // A recurring card is one expanded instance (its startDate is the
        // instance start), so it names the instance the modal opens at.
        onClick={() =>
          onEventClick(event, event.rrule ? getInstanceStart(event) : undefined)
        }
      >
        {content}
      </button>
    );
  }

  return <div style={rootStyle}>{content}</div>;
};
